import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './marketing.css';

const TEAM = [
  { name: 'Stes Tamtachew', role: 'Founder & CEO', bio: 'AI engineer and entrepreneur with a passion for automating businesses. Helps companies scale by replacing manual work with intelligent systems.', avatar: 'ST' },
  { name: 'Engineering Team', role: 'AI & Automation Engineers', bio: 'A dedicated team of senior engineers specialising in LLMs, workflow automation, algo trading, and full-stack AI product development.', avatar: 'ET' },
];

const VALUES = [
  { icon: '🎯', title: 'Results First', desc: 'We measure success by ROI delivered, not hours billed. Every automation must prove its value within 60 days.' },
  { icon: '🔬', title: 'Engineering Rigour', desc: 'We build production-grade systems — not fragile scripts. Everything is tested, monitored, and maintained.' },
  { icon: '🤝', title: 'Radical Transparency', desc: 'No jargon, no hidden fees, no surprises. You always know exactly what we\'re building and why.' },
  { icon: '⚡', title: 'Speed Without Shortcuts', desc: 'We move fast but never cut corners. Most automations are live in 2–4 weeks without sacrificing quality.' },
];

const MILESTONES = [
  { year: '2023', event: 'Gantima founded with a mission to democratise AI automation for SMBs.' },
  { year: '2024', event: 'First 10 clients onboarded. Automated $2M+ in manual workflows.' },
  { year: '2025', event: 'Launched algo trading division. Expanded to serve clients in 3 continents.' },
  { year: '2026', event: 'Scaling to 50+ clients. Building proprietary AI automation platform.' },
];

export default function About() {
  const navigate = useNavigate();

  return (
    <div className="mkt-root">
      <Helmet>
        <title>About Gantima | AI Automation Agency — Our Story, Team & Values</title>
        <meta name="description" content="Learn about Gantima — an AI automation agency founded to help businesses eliminate manual work and scale with intelligent systems. Meet the team and discover our values." />
        <meta name="keywords" content="about gantima, gantima team, AI automation agency story, who is gantima, gantima software about, AI automation company, gantima founder" />
        <link rel="canonical" href="https://www.gantima.com/about" />
        <meta property="og:title" content="About Gantima | AI Automation Agency" />
        <meta property="og:description" content="We help businesses eliminate manual work and scale intelligently with custom AI automation. Meet the team behind Gantima." />
        <meta property="og:url" content="https://www.gantima.com/about" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Gantima Software",
          "url": "https://www.gantima.com",
          "logo": "https://www.gantima.com/favicon.ico",
          "description": "AI automation agency helping businesses eliminate manual work and scale intelligently with custom chatbots, workflow automation, and algo trading systems.",
          "email": "support@gantima.com",
          "foundingDate": "2023",
          "founder": { "@type": "Person", "name": "Stes Tamtachew" },
          "sameAs": ["https://www.gantima.com"]
        })}</script>
      </Helmet>

      {/* Nav */}
      <nav className="mkt-nav">
        <a href="/" className="mkt-nav-brand" onClick={e => { e.preventDefault(); navigate('/'); }}>
          <img src="/GANTIMA-Logo.png" alt="Gantima" className="mkt-nav-logo-left-img" />
          <span className="mkt-nav-brand-tag">SOFTWARE AUTOMATION</span>
        </a>
        
        <ul className="mkt-nav-links">
          <li><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Home</a></li>
          <li><a href="/services" onClick={e => { e.preventDefault(); navigate('/services'); }}>Services</a></li>
          <li><a href="/pricing" onClick={e => { e.preventDefault(); navigate('/pricing'); }}>Pricing</a></li>
          <li className="mkt-nav-dropdown">
            <span className="mkt-nav-dropdown-trigger">Products ▾</span>
            <div className="mkt-nav-dropdown-menu">
              <a href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer" className="mkt-nav-dropdown-featured">
                <div>
                  <div className="mkt-nav-dropdown-name">ViralVolt.ai</div>
                  <div className="mkt-nav-dropdown-desc">AI Social Media Marketing Tool</div>
                </div>
              </a>
              <div className="mkt-nav-dropdown-label">Solutions by Industry</div>
              <div className="mkt-nav-dropdown-grid">
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>E-Commerce</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>HealthTech</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>FinTech & Insurance</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Algorithmic Trading</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Crypto & DeFi</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Legal & Compliance</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Real Estate</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>HR & Recruiting</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Logistics & Supply Chain</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>SaaS & Tech</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Hedge Funds & Prop Trading</a>
                <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Forex & Futures</a>
                <a href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer" className="viralvolt-link">Social Media Marketing</a>
              </div>
            </div>
          </li>
              <li><a href="/about" onClick={e => { e.preventDefault(); navigate('/about'); }}>About</a></li>
          <li><a href="/careers" onClick={e => { e.preventDefault(); navigate('/careers'); }}>Careers</a></li>
        </ul>
        <div className="mkt-nav-actions">
          <button className="mkt-btn mkt-btn-primary mkt-btn-glow" onClick={() => navigate('/')}>Book Free Consultation</button>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ padding: '130px 5% 80px', background: '#020817', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', width: '600px', height: '600px', borderRadius: '50%', background: 'rgba(124,58,237,0.08)', filter: 'blur(80px)', top: '-100px', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="mkt-pill" style={{ marginBottom: '20px' }}>ABOUT GANTIMA</div>
          <h1 style={{ fontSize: 'clamp(2.2rem,4.5vw,3.6rem)', fontWeight: 900, color: '#f1f5f9', lineHeight: 1.1, marginBottom: '20px' }}>
            We Build AI Systems<br /><span className="mkt-gradient-text">That Replace Manual Work</span>
          </h1>
          <p style={{ fontSize: '1.1rem', color: '#94a3b8', maxWidth: '600px', margin: '0 auto 36px', lineHeight: 1.75 }}>
            Gantima is an AI automation agency. We engineer custom chatbots, workflow automations, and algo trading systems that save businesses hundreds of hours every month.
          </p>
          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="mkt-btn mkt-btn-primary mkt-btn-lg mkt-btn-glow" onClick={() => navigate('/')}>Work With Us</button>
            <button className="mkt-btn mkt-btn-ghost mkt-btn-lg" onClick={() => navigate('/services')}>Our Services →</button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section style={{ background: '#030d1d', padding: '60px 5%' }}>
        <div className="mkt-container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: '24px', maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            {[
              { num: '50+', label: 'Automations Deployed' },
              { num: '$2M+', label: 'Manual Work Automated' },
              { num: '3', label: 'Continents Served' },
              { num: '60 days', label: 'Avg. Time to ROI' },
            ].map((s, i) => (
              <div key={i} style={{ padding: '28px 20px', background: 'rgba(79,142,247,0.06)', border: '1px solid rgba(79,142,247,0.15)', borderRadius: '16px' }}>
                <div style={{ fontSize: '2.2rem', fontWeight: 900, background: 'linear-gradient(135deg,#4f8ef7,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '8px' }}>{s.num}</div>
                <div style={{ fontSize: '.85rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="mkt-section" style={{ background: '#020817' }}>
        <div className="mkt-container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'center', maxWidth: '1100px', margin: '0 auto' }}>
            <div>
              <div className="mkt-pill" style={{ marginBottom: '16px' }}>OUR MISSION</div>
              <h2 style={{ fontSize: 'clamp(1.8rem,3vw,2.6rem)', fontWeight: 900, color: '#f1f5f9', lineHeight: 1.15, marginBottom: '20px' }}>
                Automate the Routine.<br /><span className="mkt-gradient-text">Amplify the Human.</span>
              </h2>
              <p style={{ color: '#94a3b8', lineHeight: 1.8, marginBottom: '16px' }}>
                Too many businesses are trapped in repetitive manual work — copy-pasting data, chasing invoices, answering the same support questions 200 times a day. We exist to fix that.
              </p>
              <p style={{ color: '#94a3b8', lineHeight: 1.8 }}>
                We build AI systems that handle the repetitive so your team can focus on what actually drives growth: strategy, relationships, and creativity.
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {VALUES.map((v, i) => (
                <div key={i} style={{ padding: '24px 20px', background: 'rgba(79,142,247,0.04)', border: '1px solid rgba(79,142,247,0.12)', borderRadius: '16px' }}>
                  <div style={{ fontSize: '1.8rem', marginBottom: '10px' }}>{v.icon}</div>
                  <div style={{ fontSize: '.9rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '8px' }}>{v.title}</div>
                  <div style={{ fontSize: '.8rem', color: '#64748b', lineHeight: 1.6 }}>{v.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Story / Timeline */}
      <section className="mkt-section" style={{ background: '#030d1d' }}>
        <div className="mkt-container">
          <div className="mkt-section-header">
            <div className="mkt-pill">OUR STORY</div>
            <h2>How We Got Here</h2>
          </div>
          <div style={{ maxWidth: '700px', margin: '0 auto', position: 'relative' }}>
            <div style={{ position: 'absolute', left: '20px', top: 0, bottom: 0, width: '2px', background: 'linear-gradient(to bottom,#4f8ef7,#7c3aed)', borderRadius: '2px' }} />
            {MILESTONES.map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: '32px', marginBottom: '36px', position: 'relative' }}>
                <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: 'linear-gradient(135deg,#4f8ef7,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.7rem', fontWeight: 900, color: '#fff', flexShrink: 0, zIndex: 1 }}>{m.year}</div>
                <div style={{ paddingTop: '10px' }}>
                  <p style={{ color: '#94a3b8', lineHeight: 1.7, margin: 0 }}>{m.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="mkt-section" style={{ background: '#020817' }}>
        <div className="mkt-container">
          <div className="mkt-section-header">
            <div className="mkt-pill">THE TEAM</div>
            <h2>People Behind Gantima</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '24px', maxWidth: '800px', margin: '0 auto' }}>
            {TEAM.map((t, i) => (
              <div key={i} style={{ padding: '32px', background: 'rgba(79,142,247,0.04)', border: '1px solid rgba(79,142,247,0.12)', borderRadius: '20px', textAlign: 'center' }}>
                <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'linear-gradient(135deg,#4f8ef7,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: 900, color: '#fff', margin: '0 auto 16px' }}>{t.avatar}</div>
                <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#f1f5f9', marginBottom: '4px' }}>{t.name}</div>
                <div style={{ fontSize: '.8rem', color: '#4f8ef7', fontWeight: 600, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '.05em' }}>{t.role}</div>
                <p style={{ fontSize: '.85rem', color: '#64748b', lineHeight: 1.7, margin: 0 }}>{t.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mkt-cta-banner">
        <div className="mkt-cta-orb mkt-cta-orb-1" /><div className="mkt-cta-orb mkt-cta-orb-2" />
        <div className="mkt-cta-inner">
          <div className="mkt-cta-badge">🤝 LET'S TALK</div>
          <h2>Ready to Automate Your Business?</h2>
          <p>Book a free 30-minute strategy call. We'll map your workflows, identify automation opportunities, and give you a custom ROI estimate — no commitment required.</p>
          <div className="mkt-cta-actions">
            <button className="mkt-btn mkt-btn-primary mkt-btn-xl mkt-btn-glow" onClick={() => navigate('/')}>Book Free Strategy Call</button>
            <span className="mkt-cta-note">Free · No commitment · Results in 60 days</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mkt-footer">
        <div className="mkt-container">
          <div className="mkt-footer-grid">
            <div className="mkt-footer-brand">
              <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }} style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'14px', textDecoration:'none' }}>
                <img src="/GANTIMA-Logo.png" alt="Gantima" style={{ height:'48px', width:'auto', objectFit:'contain', filter:'drop-shadow(0 0 8px rgba(79,142,247,0.4))', flexShrink:0 }} />
                <span style={{ fontSize:'.55rem', fontWeight:800, color:'#4f8ef7', letterSpacing:'2.5px', textTransform:'uppercase', borderLeft:'1px solid rgba(79,142,247,0.25)', paddingLeft:'10px', lineHeight:1.5 }}>SOFTWARE<br/>AUTOMATION</span>
              </a>
              <p>AI automation agency helping businesses eliminate manual work and scale intelligently.</p>
              <a href="mailto:support@gantima.com" className="mkt-footer-email">support@gantima.com</a>
            </div>
            <div className="mkt-footer-col"><h4>Services</h4><ul>
              <li><a href="/services" onClick={e => { e.preventDefault(); navigate('/services'); }}>AI Chatbot Development</a></li>
              <li><a href="/services" onClick={e => { e.preventDefault(); navigate('/services'); }}>Workflow Automation</a></li>
              <li><a href="/services" onClick={e => { e.preventDefault(); navigate('/services'); }}>Trading Bot</a></li>
            </ul></div>
            <div className="mkt-footer-col"><h4>Company</h4><ul>
              <li><a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}>Home</a></li>
              <li><a href="https://www.viralvolt.ai/" target="_blank" rel="noopener noreferrer">ViralVolt.ai ⚡</a></li>
              <li><a href="/about" onClick={e => { e.preventDefault(); navigate('/about'); }}>About</a></li>
              <li><a href="/careers" onClick={e => { e.preventDefault(); navigate('/careers'); }}>Careers</a></li>
            </ul></div>
            <div className="mkt-footer-col"><h4>Contact</h4><ul>
              <li><a href="mailto:support@gantima.com">support@gantima.com</a></li>
            </ul></div>
          </div>
          <div className="mkt-footer-bottom">
            <span>© 2026 Gantima Software. All rights reserved.</span>
            <a href="/privacy" onClick={e => { e.preventDefault(); navigate('/privacy'); }} style={{ color: '#64748b' }}>Privacy Policy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
